/**
 * Quantum Simulation Engine
 *
 * Simplified probabilistic engine for education.
 * Uses Monte Carlo sampling and wave optics formulas –
 * no heavy linear-algebra dependencies required.
 */

import {
  QuantumState,
  ParticlePath,
  SimulationResult,
  ExperimentType,
  QuantumConcept,
} from '../types';

// ─── Math helpers ─────────────────────────────────────────────────────────────

const mod2 = (re: number, im: number): number => re * re + im * im;

/** Multiply complex numbers */
const cmul = (
  [ar, ai]: [number, number],
  [br, bi]: [number, number]
): [number, number] => [ar * br - ai * bi, ar * bi + ai * br];

/** Add complex numbers */
const cadd = (
  [ar, ai]: [number, number],
  [br, bi]: [number, number]
): [number, number] => [ar + br, ai + bi];

/** Complex exponential e^(iθ) */
const cexp = (theta: number): [number, number] => [
  Math.cos(theta),
  Math.sin(theta),
];

// ─── Initial State Constructors ───────────────────────────────────────────────

export function groundState(): QuantumState {
  return {
    alpha: [1, 0],
    beta: [0, 0],
    phase: 0,
    measured: false,
  };
}

export function superpositionState(): QuantumState {
  const inv = 1 / Math.sqrt(2);
  return {
    alpha: [inv, 0],
    beta: [inv, 0],
    phase: 0,
    measured: false,
  };
}

// ─── Gate Operations ──────────────────────────────────────────────────────────

/** Hadamard gate: maps |0⟩ → |+⟩, |1⟩ → |−⟩ */
export function applyHadamard(state: QuantumState): QuantumState {
  const inv = 1 / Math.sqrt(2);
  const newAlpha = cadd(
    [inv * state.alpha[0], inv * state.alpha[1]],
    [inv * state.beta[0], inv * state.beta[1]]
  );
  const newBeta = cadd(
    [inv * state.alpha[0], inv * state.alpha[1]],
    [-inv * state.beta[0], -inv * state.beta[1]]
  );
  return { ...state, alpha: newAlpha, beta: newBeta, measured: false };
}

/** Phase shift gate: applies e^(iφ) to |1⟩ component */
export function applyPhaseShift(
  state: QuantumState,
  phiDeg: number
): QuantumState {
  const phi = (phiDeg * Math.PI) / 180;
  const newBeta = cmul(state.beta, cexp(phi));
  return { ...state, beta: newBeta, phase: state.phase + phi };
}

/** Measurement – collapses state, returns 0 or 1 */
export function measureState(state: QuantumState): QuantumState {
  const p0 = mod2(state.alpha[0], state.alpha[1]);
  const measured = Math.random() < p0 ? 0 : 1;
  return {
    ...state,
    measured: true,
    measuredValue: measured,
    alpha: measured === 0 ? [1, 0] : [0, 0],
    beta: measured === 0 ? [0, 0] : [1, 0],
  };
}

/** Beam splitter: 50/50 split, returns [transmitted, reflected] phases */
export function beamSplitter(inPhase: number): {
  transmitted: number;
  reflected: number;
} {
  return {
    transmitted: inPhase,
    reflected: inPhase + Math.PI / 2,
  };
}

// ─── Double-Slit Interference ─────────────────────────────────────────────────

interface SlitParams {
  slitSeparation: number; // d, in pixels / arbitrary units
  wavelength: number;     // λ, same units
  screenDistance: number; // L, same units
  numPoints: number;      // detector screen resolution
  measured: boolean;      // whether which-path detector is active
}

/**
 * Returns normalised intensity array for a double-slit screen.
 * When measured=true the interference term is zeroed (collapse).
 */
export function doubleSlit(params: SlitParams): number[] {
  const { slitSeparation, wavelength, screenDistance, numPoints, measured } =
    params;

  // For the measured (classical) case we zoom in to 2× the slit separation so
  // the two Gaussian blobs are clearly separated on screen.  For the quantum
  // (interference) case we use 3× to show more fringes.
  const halfWidth = measured ? slitSeparation * 2 : slitSeparation * 3;

  // Blob width for the classical case: σ = d/4 gives peak-to-valley contrast
  // of ~65 million× so the two humps are unmistakable.
  const sigma = slitSeparation / 4;

  const intensities: number[] = [];

  for (let i = 0; i < numPoints; i++) {
    const y = -halfWidth + (2 * halfWidth * i) / (numPoints - 1);

    if (measured) {
      // Which-path known → classical incoherent sum of two single-slit
      // Gaussian diffraction envelopes, one centred on each slit projection.
      const dy1 = y - slitSeparation / 2;
      const dy2 = y + slitSeparation / 2;
      const twoSigSq = 2 * sigma * sigma;
      const blob1 = Math.exp(-(dy1 * dy1) / twoSigSq);
      const blob2 = Math.exp(-(dy2 * dy2) / twoSigSq);
      intensities.push(blob1 + blob2);
    } else {
      // Quantum: amplitudes add coherently → interference fringes.
      const r1 = Math.sqrt(screenDistance * screenDistance + (y - slitSeparation / 2) * (y - slitSeparation / 2));
      const r2 = Math.sqrt(screenDistance * screenDistance + (y + slitSeparation / 2) * (y + slitSeparation / 2));
      const k = (2 * Math.PI) / wavelength;
      const amp1r = Math.cos(k * r1) / r1;
      const amp1i = Math.sin(k * r1) / r1;
      const amp2r = Math.cos(k * r2) / r2;
      const amp2i = Math.sin(k * r2) / r2;
      intensities.push(
        (amp1r + amp2r) * (amp1r + amp2r) + (amp1i + amp2i) * (amp1i + amp2i)
      );
    }
  }

  // Normalise to [0,1]
  const max = Math.max(...intensities);
  return max > 0 ? intensities.map((v) => v / max) : intensities;
}

// ─── Monte Carlo Particle Sampling ───────────────────────────────────────────

/**
 * Sample N detector hit positions using the intensity distribution as a PDF.
 * Returns (x, y) pairs on the detector screen.
 */
export function sampleDetectorHits(
  intensities: number[],
  numParticles: number,
  screenWidth: number,
  screenHeight: number
): { x: number; y: number; intensity: number }[] {
  const hits: { x: number; y: number; intensity: number }[] = [];
  const cumulative: number[] = [];
  let sum = 0;

  for (const v of intensities) {
    sum += v;
    cumulative.push(sum);
  }

  for (let p = 0; p < numParticles; p++) {
    const r = Math.random() * sum;
    let idx = cumulative.findIndex((c) => c >= r);
    if (idx < 0) idx = intensities.length - 1;

    const x =
      (screenWidth * 0.1) +
      (idx / (intensities.length - 1)) * screenWidth * 0.8;
    const y =
      screenHeight * 0.1 +
      Math.random() * screenHeight * 0.8;

    hits.push({ x, y, intensity: intensities[idx] });
  }
  return hits;
}

// ─── Qubit Circuit Simulation ─────────────────────────────────────────────────

export interface QubitCircuitNode {
  type: 'source' | 'hadamard' | 'phase-shift' | 'measure';
  phiDeg?: number;
}

export function runQubitCircuit(
  circuit: QubitCircuitNode[],
  shots: number
): { zero: number; one: number; finalState: QuantumState } {
  let zeroCount = 0;
  let oneCount = 0;
  let finalState: QuantumState = groundState();

  for (let s = 0; s < shots; s++) {
    let state = groundState();
    for (const node of circuit) {
      switch (node.type) {
        case 'hadamard':
          state = applyHadamard(state);
          break;
        case 'phase-shift':
          state = applyPhaseShift(state, node.phiDeg ?? 90);
          break;
        case 'measure':
          state = measureState(state);
          break;
        default:
          break;
      }
    }
    finalState = state;
    if (state.measuredValue === 0) zeroCount++;
    else oneCount++;
  }

  return { zero: zeroCount, one: oneCount, finalState };
}

// ─── Entanglement Simulation ──────────────────────────────────────────────────

export interface EntanglementResult {
  qubit1: (0 | 1)[];
  qubit2: (0 | 1)[];
  correlationRate: number;
  jointCounts: { zeroZero: number; zeroOne: number; oneZero: number; oneOne: number };
}

/**
 * Simulate measuring the Bell state (|00⟩ + |11⟩)/√2.
 * Each shot: both qubits collapse to the same random value (0 or 1),
 * so |01⟩ and |10⟩ joint outcomes never occur.
 */
export function simulateEntanglement(shots: number): EntanglementResult {
  const q1: (0 | 1)[] = [];
  const q2: (0 | 1)[] = [];
  const jointCounts = { zeroZero: 0, zeroOne: 0, oneZero: 0, oneOne: 0 };

  for (let s = 0; s < shots; s++) {
    const v: 0 | 1 = Math.random() < 0.5 ? 0 : 1;
    q1.push(v);
    q2.push(v); // perfect correlation – Bell state collapses both qubits identically
    if (v === 0) jointCounts.zeroZero++; else jointCounts.oneOne++;
  }

  return { qubit1: q1, qubit2: q2, correlationRate: 1, jointCounts };
}

// ─── High-Level Experiment Runner ─────────────────────────────────────────────

export interface ExperimentConfig {
  type: ExperimentType;
  numParticles: number;
  withMeasurement?: boolean;     // double-slit: detector at slit
  phaseShiftDeg?: number;        // phase shifter angle
  slitSeparation?: number;
  wavelength?: number;
  screenDistance?: number;
  screenWidth?: number;
  screenHeight?: number;
  circuitNodes?: QubitCircuitNode[];
}

export function runExperiment(config: ExperimentConfig): SimulationResult {
  const {
    type,
    numParticles,
    withMeasurement = false,
    phaseShiftDeg = 0,
    slitSeparation = 60,
    wavelength = 20,
    screenDistance = 200,
    screenWidth = 400,
    screenHeight = 300,
    circuitNodes = [],
  } = config;

  let detectorHits: { x: number; y: number; intensity: number }[] = [];
  let probabilityDistribution: number[] = [];
  let measurementCounts = { zero: 0, one: 0 };
  let hasInterference = false;
  let explanation = '';
  let concepts: QuantumConcept[] = [];
  const particles: ParticlePath[] = [];
  let jointCountsResult: SimulationResult['jointCounts'];

  switch (type) {
    case 'double-slit': {
      probabilityDistribution = doubleSlit({
        slitSeparation,
        wavelength,
        screenDistance,
        numPoints: 120,
        measured: withMeasurement,
      });
      detectorHits = sampleDetectorHits(
        probabilityDistribution,
        numParticles,
        screenWidth,
        screenHeight
      );
      hasInterference = !withMeasurement;
      explanation = withMeasurement
        ? 'You placed a detector at the slit! This measured which path the particle took. Because of measurement, the quantum superposition collapsed – and the interference pattern disappeared. The particle behaved like a classical object.'
        : 'Each particle travels through both slits at once (superposition)! The two paths interfere with each other, creating bright and dark bands on the screen. This is the famous interference pattern – proof of wave-particle duality.';
      concepts = withMeasurement
        ? ['measurement', 'wave-particle-duality', 'decoherence']
        : ['superposition', 'interference', 'wave-particle-duality'];
      break;
    }

    case 'measurement-collapse': {
      probabilityDistribution = doubleSlit({
        slitSeparation,
        wavelength,
        screenDistance,
        numPoints: 120,
        measured: true,
      });
      detectorHits = sampleDetectorHits(
        probabilityDistribution,
        numParticles,
        screenWidth,
        screenHeight
      );
      hasInterference = false;
      explanation =
        'Measurement collapses superposition! Once we know which path the particle takes, it can no longer interfere with itself. The striped pattern vanishes and we see a simple two-blob pattern instead.';
      concepts = ['measurement', 'decoherence', 'wave-particle-duality'];
      break;
    }

    case 'beam-splitter': {
      // 50/50 split visualised as two probability paths
      const n = Math.round(numParticles / 2);
      measurementCounts = { zero: n, one: numParticles - n };
      probabilityDistribution = [0.5, 0.5];
      explanation =
        'The beam splitter puts the photon into superposition! It takes BOTH paths at the same time with equal probability. Only when measured does it "choose" one path – randomly, 50/50.';
      concepts = ['superposition', 'randomness', 'wave-particle-duality'];
      hasInterference = false;
      break;
    }

    case 'qubit-circuit': {
      const nodes: QubitCircuitNode[] =
        circuitNodes.length > 0
          ? circuitNodes
          : [{ type: 'source' }, { type: 'hadamard' }, { type: 'measure' }];
      const result = runQubitCircuit(nodes, numParticles);
      measurementCounts = { zero: result.zero, one: result.one };
      probabilityDistribution = [
        result.zero / numParticles,
        result.one / numParticles,
      ];
      explanation =
        'The Hadamard gate creates superposition! The qubit starts as |0⟩ and after the Hadamard gate it is in an equal mix of |0⟩ and |1⟩. When measured, it randomly gives 0 or 1 – roughly 50/50 over many shots.';
      concepts = ['superposition', 'randomness', 'measurement'];
      hasInterference = false;
      break;
    }

    case 'entanglement': {
      const entResult = simulateEntanglement(numParticles);
      measurementCounts = {
        zero: entResult.jointCounts.zeroZero,
        one: entResult.jointCounts.oneOne,
      };
      probabilityDistribution = [0.5, 0.5];
      explanation =
        `Both qubits were measured ${numParticles} times. Every single time they agreed – either both 0 or both 1. The "mismatched" outcomes |01⟩ and |10⟩ never occurred. This is quantum entanglement: measuring one qubit instantly determines the other, no matter how far apart they are!`;
      concepts = ['entanglement', 'randomness', 'measurement'];
      hasInterference = false;
      jointCountsResult = entResult.jointCounts;
      break;
    }

    default: {
      explanation = 'Run an experiment to see results!';
      concepts = [];
    }
  }

  return {
    experimentType: type,
    particles,
    detectorHits,
    probabilityDistribution,
    measurementCounts,
    hasInterference,
    explanation,
    conceptsIllustrated: concepts,
    jointCounts: jointCountsResult,
    correlationRate: jointCountsResult ? 1 : undefined,
  };
}
